import React, {FC, useState, useEffect, useCallback} from "react";
import {withResizeDetector} from "react-resize-detector";
import _ from "lodash";
import ProgressBar from "../../../common/progress-bar/ProgressBar";

// Components
import WidgetHeader from "../widget-header/WidgetHeader";
import WidgetHeaderButton from "../widget-header/widget-header-button/WidgetHeaderButton";
import ModalDefault from "../../../common/modal/modal-default";
import WidgetEmpty from "../empty-widget-content/empty-widget-content";
import Loader from "../../../common/loader/Loader";
import ROIConfigurationForm from "./ROIConfigurationForm";
import ROIFilterModal, {ROIFilterValues} from "./ROIFilterModal";
import TopAutomatedTasksView from "./TopAutomatedTasksView";
import {processTopAutomatedTasks} from "./utils/top-tasks-processor";

// Types and Constants
import {IROIWidgetProps, IROIWidgetConfig} from "./models/roi-widget.props";
import {IROIData, ContentType} from "./models/roi-widget.state";
import {INITIAL_VALUES, formatCurrency} from "./constants";

// Services
import {ROIDataService, IROICalculationResult} from "./services/roi-data.service";
import {ROIConfigService} from "./services/roi-config.service";

// Date Range Selector
import DateRangeSelector, {
    IDateRange,
    dateToStartOfDayTimestamp,
    dateToEndOfDayTimestamp,
} from "./DateRangeSelector";

// Chart Components
import ROIChartContainer from "./charts/ROIChartContainer";

// Helpers
import {isUserWithDashboardViewerRole} from "../../../../helpers/dashboard-helpers";

// Services
import UserService from "../../../../services/user-service";
import {steveDataStorage} from "../../../../helpers/sdk-helpers";

// Notifications
import {info, success, error as showError} from "../../../../helpers/notifications";

// Styles
import "./roi-widget.scss";
import "./roi-filter-modal.scss";

const ROIWidget: React.FC<IROIWidgetProps> = ({
    data,
    isEditAllowed,
    onUpdate,
    showWidgetInfo,
    onRemove,
    onClone,
    width = 400,
    height = 300,
}) => {
    // Props are already destructured in function parameters

    const [isLoading, setIsLoading] = useState(false);
    const [contentType, setContentType] = useState<ContentType>(ContentType.EMPTY);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [tempConfigData, setTempConfigData] = useState<IROIWidgetConfig>(
        _.cloneDeep(INITIAL_VALUES)
    );
    const [configData, setConfigData] = useState<IROIWidgetConfig>(_.cloneDeep(INITIAL_VALUES));
    const [roiData, setRoiData] = useState<IROIData | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false); // Prevent concurrent refreshes
    const [error, setError] = useState<string | null>(null);
    const [filterValues, setFilterValues] = useState<ROIFilterValues>({sites: [], roles: []});

    // Date range state - initialize from config or default to last 6 months
    const getDefaultDateRange = (): IDateRange => {
        const toDate = new Date();
        const fromDate = new Date();
        fromDate.setMonth(toDate.getMonth() - 6);
        return {
            fromDate,
            toDate,
            label: "Last 6 months",
        };
    };

    const getDateRangeFromConfig = (config: IROIWidgetConfig): IDateRange => {
        if (config.dateRange) {
            return {
                fromDate: new Date(config.dateRange.fromDate),
                toDate: new Date(config.dateRange.toDate),
                label: `${new Date(config.dateRange.fromDate).toLocaleDateString()} - ${new Date(
                    config.dateRange.toDate
                ).toLocaleDateString()}`,
            };
        }
        return getDefaultDateRange();
    };

    const [dateRange, setDateRange] = useState<IDateRange>(getDefaultDateRange());
    const [selectedMetric, setSelectedMetric] = useState("time_saved");
    const [showComparisonView, setShowComparisonView] = useState(false);

    // Helper function to save view preference
    const saveViewPreference = useCallback(
        (viewMode: boolean) => {
            try {
                const widgetId = data?.id || "roi-widget";
                steveDataStorage("dashboards", "ROIWidgetPreferences", true).set(
                    viewMode,
                    `${widgetId}_viewMode`
                );
            } catch (error) {
                console.error("Error saving view preference:", error);
            }
        },
        [data?.id]
    );

    // Enhanced function to toggle comparison view and save preference
    const toggleComparisonView = useCallback(() => {
        const newViewMode = !showComparisonView;
        setShowComparisonView(newViewMode);
        saveViewPreference(newViewMode);
    }, [showComparisonView, saveViewPreference]);

    // Initialize widget configuration from backend data
    useEffect(() => {
        const widgetConfig = data?.config || {};

        // Check if widget has any meaningful configuration
        const hasConfiguration =
            widgetConfig &&
            !_.isEmpty(widgetConfig) &&
            (widgetConfig.title ||
                widgetConfig.metric ||
                widgetConfig.visualizationMode ||
                widgetConfig.compareTo ||
                widgetConfig.globalSettings ||
                widgetConfig.taskConfiguration ||
                widgetConfig.activities ||
                Object.keys(widgetConfig).length > 0);

        if (hasConfiguration) {
            // Widget has been configured - load from backend

            // Convert backend config to UI format first
            const uiConfig = ROIConfigService.convertToUIFormat(widgetConfig);

            // Merge with initial values and ensure title from widget data is included
            const mergedConfig = {
                ...INITIAL_VALUES,
                ...uiConfig,
                title: data?.title || uiConfig.title || INITIAL_VALUES.title,
            };

            // Ensure comparison settings are preserved exactly as they come from backend
            if (uiConfig.compareTo) {
                mergedConfig.compareTo = uiConfig.compareTo;
            }

            setConfigData(mergedConfig);
            setTempConfigData(_.cloneDeep(mergedConfig));

            // Try to load saved date range preference using steveDataStorage
            let dateRangeToUse = null;
            try {
                const widgetId = data?.id || "roi-widget";
                const savedPreference = steveDataStorage(
                    "dashboards",
                    "ROIWidgetPreferences",
                    true
                ).get(`${widgetId}_dateRange`);

                if (savedPreference) {
                    dateRangeToUse = {
                        fromDate: new Date(savedPreference.fromDate),
                        toDate: new Date(savedPreference.toDate),
                        label: savedPreference.period || "Custom",
                    };
                }
            } catch (error) {
                console.error("Error loading date range preference:", error);
            }

            // Try to load saved filter preference using steveDataStorage
            let filterPreferenceToUse = null;
            try {
                const widgetId = data?.id || "roi-widget";
                const savedFilterPreference = steveDataStorage(
                    "dashboards",
                    "ROIWidgetPreferences",
                    true
                ).get(`${widgetId}_filters`);

                if (savedFilterPreference) {
                    filterPreferenceToUse = {
                        sites: savedFilterPreference.sites || [],
                        roles: savedFilterPreference.roles || [],
                    };
                }
            } catch (error) {
                console.error("Error loading filter preference:", error);
            }

            // Try to load saved view preference (comparison view toggle)
            try {
                const widgetId = data?.id || "roi-widget";
                const savedViewPreference = steveDataStorage(
                    "dashboards",
                    "ROIWidgetPreferences",
                    true
                ).get(`${widgetId}_viewMode`);

                if (savedViewPreference !== null && savedViewPreference !== undefined) {
                    setShowComparisonView(savedViewPreference);
                }
            } catch (error) {
                console.error("Error loading view preference:", error);
            }

            // Initialize filter values from preference
            if (filterPreferenceToUse) {
                setFilterValues(filterPreferenceToUse);
            }

            // Initialize date range from preference or config
            if (dateRangeToUse) {
                setDateRange(dateRangeToUse);

                // Update the config with the preferred date range
                const configWithPreferredRange = {
                    ...mergedConfig,
                    dateRange: {
                        fromDate: dateToStartOfDayTimestamp(dateRangeToUse.fromDate),
                        toDate: dateToEndOfDayTimestamp(dateRangeToUse.toDate),
                        period: dateRangeToUse.label || "Custom",
                    },
                };
                setConfigData(configWithPreferredRange);

                setContentType(ContentType.DATA);
                // Call refreshData with the config that includes preferred date range and filters
                const filterParams =
                    filterPreferenceToUse &&
                    (filterPreferenceToUse.sites.length > 0 ||
                        filterPreferenceToUse.roles.length > 0)
                        ? {
                              siteIds: filterPreferenceToUse.sites.map((site: any) => site.value),
                              actionGroupIds: filterPreferenceToUse.roles.map(
                                  (role: any) => role.value
                              ),
                          }
                        : undefined;
                setTimeout(() => refreshData(configWithPreferredRange, filterParams), 100);
            } else {
                const initialDateRange = getDateRangeFromConfig(mergedConfig);
                setDateRange(initialDateRange);

                setContentType(ContentType.DATA);
                // Call refreshData with the original config and filters
                const filterParams =
                    filterPreferenceToUse &&
                    (filterPreferenceToUse.sites.length > 0 ||
                        filterPreferenceToUse.roles.length > 0)
                        ? {
                              siteIds: filterPreferenceToUse.sites.map((site: any) => site.value),
                              actionGroupIds: filterPreferenceToUse.roles.map(
                                  (role: any) => role.value
                              ),
                          }
                        : undefined;
                setTimeout(() => refreshData(mergedConfig, filterParams), 100);
            }
        } else {
            // New widget - show empty state for configuration
            setConfigData(_.cloneDeep(INITIAL_VALUES));
            setTempConfigData(_.cloneDeep(INITIAL_VALUES));
            setContentType(ContentType.EMPTY);
        }
    }, [data?.config, data?.title]);

    // Ensure tempConfigData is synchronized with configData
    useEffect(() => {
        setTempConfigData(_.cloneDeep(configData));
    }, [configData]);

    const refreshData = useCallback(
        async (
            overrideConfig?: IROIWidgetConfig,
            filters?: {siteIds?: string[]; actionGroupIds?: string[]}
        ) => {
            const activeConfig = overrideConfig || configData;

            // Skip if no valid config
            if (!activeConfig || !activeConfig.globalSettings?.averageHourlyRate) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Use the date range from config if available, otherwise fall back to state
                const configDateRange = activeConfig.dateRange;
                const effectiveDateRange = configDateRange
                    ? {
                          fromDate: new Date(configDateRange.fromDate),
                          toDate: new Date(configDateRange.toDate),
                      }
                    : dateRange;

                // Convert to Unix timestamps for API call
                const fromDate = new Date(dateToStartOfDayTimestamp(effectiveDateRange.fromDate));
                const toDate = new Date(dateToEndOfDayTimestamp(effectiveDateRange.toDate));

                // Get tenant ID from user service
                const tenantId = UserService.getCurrentOrganizationId();

                let activityData;

                // Fetch ROI data with comparison support
                const roiCalculation: IROICalculationResult =
                    await ROIDataService.fetchROIDataWithComparison(
                        tenantId,
                        fromDate,
                        toDate,
                        activeConfig,
                        filters
                    );

                // Convert to widget data format - use backend calculated values
                const roiWidgetData: IROIData = {
                    totalActivities: roiCalculation.totalActivities,
                    totalHoursSaved: roiCalculation.totalHoursSaved,
                    totalCostSaved: roiCalculation.totalCostSaved,
                    roi: roiCalculation.roi,
                    roiPercentage: roiCalculation.roiPercentage,
                    totalInvestment: roiCalculation.totalInvestment,
                    totalReturn: roiCalculation.totalReturn,
                    netProfit: roiCalculation.netProfit,
                    // activityBreakdown: roiCalculation.activityBreakdown, // Removed - not in IROIData interface
                    calculationMethod: roiCalculation.calculationMethod,
                    timeframe: roiCalculation.timeframe,
                    currency: roiCalculation.currency,
                    // Daily activities now come directly from the ROI service
                    dailyActivities: roiCalculation.dailyActivities || [],
                    // Include raw data for top automated tasks
                    rawData: roiCalculation.rawData,
                    comparison: roiCalculation.comparison
                        ? {
                              enabled: true,
                              type: roiCalculation.comparison.type || "previous_period",
                              previousData: roiCalculation.comparison.previousData
                                  ? {
                                        totalActivities:
                                            roiCalculation.comparison.previousData.totalActivities,
                                        totalHoursSaved:
                                            roiCalculation.comparison.previousData.totalHoursSaved,
                                        totalCostSaved:
                                            roiCalculation.comparison.previousData.totalCostSaved,
                                        dailyActivities:
                                            roiCalculation.comparison.previousData
                                                .dailyActivities || [],
                                    }
                                  : undefined,
                              changes: roiCalculation.comparison.changes,
                          }
                        : undefined,
                };

                setRoiData(roiWidgetData);
                setContentType(ContentType.DATA);
            } catch (err) {
                console.error("Error in refreshData:", {
                    error: err,
                    configData,
                    dateRange,
                    contentType,
                });

                // More specific error handling
                const errorMessage = err instanceof Error ? err.message : String(err);
                console.error("Setting error state with message:", errorMessage);

                setError(`Failed to calculate ROI: ${errorMessage}`);
                setContentType(ContentType.ERROR);
            } finally {
                setIsLoading(false);
            }
        },
        [configData, contentType, dateRange]
    );

    const handleConfigSubmit = useCallback(
        async (values: IROIWidgetConfig) => {
            // Update local state first
            setConfigData(values);
            setTempConfigData(_.cloneDeep(values));
            setShowConfigModal(false);

            // Clear any existing ROI data to show loading state
            setRoiData(null);
            setError(null);
            setContentType(ContentType.LOADING);

            // Update widget's data.config so it gets saved when dashboard is published
            if (onUpdate && data) {
                try {
                    // Convert UI config to backend format using the service
                    const backendConfig = ROIConfigService.convertToBackendFormat(values);

                    // Follow NetworkTools pattern - merge specific fields instead of replacing entire config
                    onUpdate({
                        ...data,
                        title: values.title || data.title || "ROI Widget",
                        config: {
                            ...(data.config || {}),
                            // Update with converted backend format
                            metric: backendConfig.metric,
                            visualizationMode: backendConfig.visualizationMode,
                            compareTo: backendConfig.compareTo,
                            hourlyRate: backendConfig.hourlyRate,
                            currencyCode: backendConfig.currencyCode,
                            activities: backendConfig.activities, // Converted from taskConfiguration
                            taskConfiguration: values.taskConfiguration || [], // Also save UI format for loading
                            reportFormat: values.reportFormat, // Save report format for download
                        },
                    });
                } catch (error) {
                    console.error("Error updating widget configuration:", error);
                }
            }

            // Refresh data with new configuration
            refreshData(values);
        },
        [onUpdate, data, refreshData]
    );

    const handleConfigCancel = useCallback(() => {
        setTempConfigData(_.cloneDeep(configData));
        setShowConfigModal(false);
    }, [configData]);

    const handleConfigShow = useCallback(() => {
        setShowConfigModal(true);
    }, []);

    const handleFormFieldChange = useCallback((field: string, value: unknown) => {
        try {
            setTempConfigData((prev) => ({
                ...prev,
                [field]: value,
            }));
        } catch (error) {
            console.error("Error in handleFormFieldChange:", error, {field, value});
        }
    }, []);

    // Filter modal handlers
    const handleFilterShow = useCallback(() => {
        setShowFilterModal(true);
    }, []);

    const handleFilterClose = useCallback(() => {
        setShowFilterModal(false);
    }, []);

    const handleFilterApply = useCallback(
        (filters: ROIFilterValues) => {
            setFilterValues(filters);

            // Save filter preferences using steveDataStorage
            try {
                const widgetId = data?.id || "roi-widget";
                const filterPreference = {
                    sites: filters.sites,
                    roles: filters.roles,
                };

                steveDataStorage("dashboards", "ROIWidgetPreferences", true).set(
                    filterPreference,
                    `${widgetId}_filters`
                );
            } catch (error) {
                console.error("Error saving filter preference:", error);
            }

            // Apply filters to data fetching logic
            if (contentType === ContentType.DATA) {
                // Extract site IDs and action group IDs from filter values
                const filterParams = {
                    siteIds: filters.sites.map((site) => site.value),
                    actionGroupIds: filters.roles.map((role) => role.value),
                };
                refreshData(configData, filterParams);
            }
        },
        [contentType, configData, refreshData, data?.id]
    );

    // Direct download handler
    const handleDirectDownload = useCallback(async () => {
        if (!configData || isDownloading) return;

        setIsDownloading(true);

        // Show generating report toast with a reasonable duration
        info({
            title: "Report Generation",
            message: "Generating report...",
            duration: 3000,
        });

        try {
            // Get current filter parameters
            const filterParams =
                filterValues.sites.length > 0 || filterValues.roles.length > 0
                    ? {
                          siteIds: filterValues.sites.map((site) => site.value),
                          actionGroupIds: filterValues.roles.map((role) => role.value),
                      }
                    : undefined;

            // Call download service for comprehensive report
            await ROIDataService.downloadComprehensiveReport(
                UserService.getCurrentOrganizationId(),
                dateRange.fromDate,
                dateRange.toDate,
                configData,
                filterParams
            );

            // Show success toast
            success({
                title: "Download Started",
                message: "Starting download...",
                duration: 3000,
            });
        } catch (downloadError) {
            console.error("Error downloading comprehensive report:", downloadError);

            // Show error toast
            showError({
                title: "Download Failed",
                message:
                    downloadError instanceof Error
                        ? downloadError.message
                        : "Failed to generate report. Please try again.",
                duration: 5000,
            });
        } finally {
            setIsDownloading(false);
        }
    }, [configData, dateRange, filterValues, isDownloading]);

    const handleFilterReset = useCallback(() => {
        const emptyFilters = {sites: [], roles: []};
        setFilterValues(emptyFilters);

        // Clear filter preferences using steveDataStorage
        try {
            const widgetId = data?.id || "roi-widget";
            steveDataStorage("dashboards", "ROIWidgetPreferences", true).set(
                emptyFilters,
                `${widgetId}_filters`
            );
        } catch (error) {
            console.error("Error clearing filter preference:", error);
        }

        // Refresh data without filters
        if (contentType === ContentType.DATA) {
            refreshData(configData);
        }
    }, [contentType, configData, refreshData, data?.id]);

    const handleDateRangeChange = useCallback(
        async (newRange: IDateRange, period?: string) => {
            setDateRange(newRange);

            // Save date range to widget configuration
            const updatedConfig = {
                ...configData,
                dateRange: {
                    fromDate: dateToStartOfDayTimestamp(newRange.fromDate),
                    toDate: dateToEndOfDayTimestamp(newRange.toDate),
                    period: period || "Custom",
                },
            };

            setConfigData(updatedConfig);

            // Save date range to user preferences using steveDataStorage
            try {
                const widgetId = data?.id || "roi-widget";
                const dateRangePreference = {
                    fromDate: newRange.fromDate.getTime(),
                    toDate: newRange.toDate.getTime(),
                    period: period || "Custom",
                };

                steveDataStorage("dashboards", "ROIWidgetPreferences", true).set(
                    dateRangePreference,
                    `${widgetId}_dateRange`
                );
            } catch (error) {
                console.error("Error saving date range preference:", error);
            }

            // DON'T save date range changes to backend - only save through form submission
            // Date range is a UI state, not a persistent configuration

            // Trigger data refresh with new date range using updated config
            if (contentType === ContentType.DATA) {
                // Preserve any active filters when refreshing with new date range
                const activeFilters =
                    filterValues.sites.length > 0 || filterValues.roles.length > 0
                        ? {
                              siteIds: filterValues.sites.map((site) => site.value),
                              actionGroupIds: filterValues.roles.map((role) => role.value),
                          }
                        : undefined;
                refreshData(updatedConfig, activeFilters); // Pass the updated config and active filters
            }
        },
        [data, onUpdate, refreshData, contentType, filterValues]
    );

    // Render content based on current state
    const renderContent = () => {
        switch (contentType) {
            case ContentType.EMPTY:
                return (
                    <WidgetEmpty
                        isEditAllowed={isEditAllowed}
                        showSettingsModal={handleConfigShow}
                        text="Configure this widget to display ROI metrics."
                    />
                );

            case ContentType.LOADING:
                return null; // Loader is handled separately

            case ContentType.DATA:
                if (!roiData) {
                    return (
                        <WidgetEmpty
                            isEditAllowed={isEditAllowed}
                            showSettingsModal={handleConfigShow}
                            text="No ROI data available. Please check your configuration."
                        />
                    );
                }
                return (() => {
                    // Render metric based on selected metric type
                    const selectedMetric = configData?.metric?.value || "time_saved";

                    // Helper function to render comparison indicator
                    const renderComparison = (metricType: "hours" | "cost" | "activities") => {
                        if (!roiData.comparison?.enabled || !roiData.comparison?.changes) {
                            return null;
                        }

                        const changes = roiData.comparison.changes;
                        const comparisonType = roiData.comparison.type;

                        let changeValue = "";
                        let changePercentage = "";

                        switch (metricType) {
                            case "hours":
                                changeValue = changes.hoursSavedChange.toLocaleString();
                                changePercentage = changes.hoursSavedPercentage;
                                break;
                            case "cost":
                                changeValue = formatCurrency(
                                    Math.abs(changes.costSavedChange),
                                    roiData.currency
                                );
                                changePercentage = changes.costSavedPercentage;
                                break;
                            case "activities":
                                changeValue = Math.abs(changes.activitiesChange).toString();
                                changePercentage = changes.activitiesPercentage;
                                break;
                        }

                        const isPositive = changePercentage.startsWith("+");
                        const comparisonText =
                            comparisonType === "previous_period"
                                ? "vs previous period"
                                : "vs same period last year";

                        return (
                            <div
                                className={`roi-widget__comparison ${
                                    isPositive
                                        ? "roi-widget__comparison--positive"
                                        : "roi-widget__comparison--negative"
                                }`}
                            >
                                {changePercentage} {comparisonText}
                            </div>
                        );
                    };

                    const renderMetric = () => {
                        // If comparison view is active, show comparison instead of metric
                        if (showComparisonView && roiData.comparison?.enabled) {
                            return renderComparisonView();
                        }

                        switch (selectedMetric) {
                            case "cost_savings":
                                return (
                                    <div className="roi-widget__metric roi-widget__metric--primary">
                                        <span className="roi-widget__metric-label">
                                            <span className="roi-widget__metric-text">
                                                Total estimated cost savings based on configured
                                                labor rates.
                                            </span>
                                            {roiData.comparison?.enabled && (
                                                <i
                                                    className={`g-icon g-icon-chart-analytics roi-widget__icon--enabled roi-widget__comparison-icon`}
                                                    onClick={() => toggleComparisonView()}
                                                    title="Show comparison chart"
                                                ></i>
                                            )}
                                        </span>
                                        <div className="roi-widget__metric-container">
                                            <span className="roi-widget__metric-value">
                                                {formatCurrency(
                                                    roiData.totalCostSaved,
                                                    roiData.currency
                                                )}
                                            </span>
                                            {renderComparison("cost")}
                                        </div>
                                    </div>
                                );
                            case "time_saved":
                                return (
                                    <div className="roi-widget__metric roi-widget__metric--primary">
                                        <span className="roi-widget__metric-label">
                                            <span className="roi-widget__metric-text">
                                                Estimated hours saved through automation
                                            </span>
                                            {roiData.comparison?.enabled && (
                                                <i
                                                    className={`g-icon g-icon-chart-analytics roi-widget__icon--enabled roi-widget__comparison-icon`}
                                                    onClick={() => toggleComparisonView()}
                                                    title="Show comparison chart"
                                                ></i>
                                            )}
                                        </span>
                                        <div className="roi-widget__metric-container">
                                            <span className="roi-widget__metric-value">
                                                {roiData.totalHoursSaved.toLocaleString()} hrs
                                            </span>
                                            {renderComparison("hours")}
                                        </div>
                                    </div>
                                );
                            case "tasks_automated":
                                return (
                                    <div className="roi-widget__metric roi-widget__metric--primary">
                                        <span className="roi-widget__metric-label">
                                            <span className="roi-widget__metric-text">
                                                Total tasks completed via automation in the selected
                                                period.
                                            </span>
                                            {roiData.comparison?.enabled && (
                                                <i
                                                    className={`g-icon g-icon-chart-analytics roi-widget__icon--enabled roi-widget__comparison-icon`}
                                                    onClick={() => toggleComparisonView()}
                                                    title="Show comparison chart"
                                                ></i>
                                            )}
                                        </span>
                                        <div className="roi-widget__metric-container">
                                            <span className="roi-widget__metric-value">
                                                {roiData.totalActivities.toLocaleString()}
                                            </span>
                                            {renderComparison("activities")}
                                        </div>
                                    </div>
                                );
                            case "top_automated_tasks": {
                                // Process activities data for top automated tasks
                                const topTasks = processTopAutomatedTasks(
                                    roiData.rawData?.report?.activities || {},
                                    roiData.rawData?.roiConfig
                                );

                                return <TopAutomatedTasksView tasks={topTasks} />;
                            }
                            default:
                                return (
                                    <div className="roi-widget__metric roi-widget__metric--primary">
                                        <span className="roi-widget__metric-label">
                                            <span className="roi-widget__metric-text">
                                                Estimated hours saved through automation
                                            </span>
                                            {roiData.comparison?.enabled && (
                                                <i
                                                    className={`g-icon g-icon-chart-analytics roi-widget__icon--enabled roi-widget__comparison-icon`}
                                                    onClick={() => toggleComparisonView()}
                                                    title="Show comparison chart"
                                                ></i>
                                            )}
                                        </span>
                                        <div className="roi-widget__metric-container">
                                            <span className="roi-widget__metric-value">
                                                {roiData.totalHoursSaved.toLocaleString()} hrs
                                            </span>
                                            {renderComparison("hours")}
                                        </div>
                                    </div>
                                );
                        }
                    };

                    // Render comparison view
                    const renderComparisonView = () => {
                        if (
                            !roiData.comparison?.enabled ||
                            !roiData.comparison?.changes ||
                            !roiData.comparison?.previousData
                        ) {
                            return null;
                        }

                        const {changes, previousData, type} = roiData.comparison;
                        const comparisonText =
                            type === "previous_period"
                                ? "Previous period"
                                : "Same period last year";

                        // Get current metric data based on selectedMetric
                        let currentValue, previousValue, unit, changePercentage;

                        switch (selectedMetric) {
                            case "cost_savings":
                                currentValue = roiData.totalCostSaved;
                                previousValue = previousData.totalCostSaved;
                                unit = "";
                                changePercentage = changes.costSavedPercentage;
                                break;
                            case "tasks_automated":
                                currentValue = roiData.totalActivities;
                                previousValue = previousData.totalActivities;
                                unit = "";
                                changePercentage = changes.activitiesPercentage;
                                break;
                            default: // time_saved and others
                                currentValue = roiData.totalHoursSaved;
                                previousValue = previousData.totalHoursSaved;
                                unit = " hrs";
                                changePercentage = changes.hoursSavedPercentage;
                        }

                        const maxValue = Math.max(currentValue, previousValue);
                        const isPositive = changePercentage.startsWith("+");

                        return (
                            <div className="roi-widget__metric roi-widget__metric--primary">
                                <span className="roi-widget__metric-label">
                                    <span className="roi-widget__metric-text">
                                        {selectedMetric === "cost_savings"
                                            ? "Total estimated cost savings based on configured labor rates."
                                            : selectedMetric === "tasks_automated"
                                            ? "Total tasks completed via automation in the selected period."
                                            : "Estimated hours saved through automation"}
                                    </span>
                                    <i
                                        className={`g-icon g-icon-chart-analytics roi-widget__icon--enabled roi-widget__comparison-icon`}
                                        onClick={() => toggleComparisonView()}
                                        title="Hide comparison chart"
                                    ></i>
                                </span>

                                <div className="roi-widget__comparison-percentage">
                                    <span
                                        className={`roi-widget__percentage-arrow ${
                                            isPositive ? "positive" : "negative"
                                        }`}
                                    >
                                        {isPositive ? "↑" : "↓"}
                                    </span>
                                    <span
                                        className={`roi-widget__percentage-value ${
                                            isPositive ? "positive" : "negative"
                                        }`}
                                    >
                                        {changePercentage}
                                    </span>
                                </div>

                                <div className="roi-widget__comparison-bars">
                                    <div className="roi-widget__bar-item">
                                        <div className="roi-widget__bar-label">
                                            <span>Current period:</span>
                                            <strong>
                                                {selectedMetric === "cost_savings"
                                                    ? formatCurrency(currentValue, roiData.currency)
                                                    : `${currentValue.toLocaleString()}${unit}`}
                                            </strong>
                                        </div>
                                        <div className="roi-widget__bar-container roi-widget__bar-container--current">
                                            <div
                                                className="roi-widget__bar roi-widget__bar--current"
                                                style={{
                                                    width:
                                                        maxValue > 0
                                                            ? `${(currentValue / maxValue) * 100}%`
                                                            : "0%",
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="roi-widget__bar-item">
                                        <div className="roi-widget__bar-label">
                                            <span>{comparisonText}:</span>
                                            <strong>
                                                {selectedMetric === "cost_savings"
                                                    ? formatCurrency(
                                                          previousValue,
                                                          roiData.currency
                                                      )
                                                    : `${previousValue.toLocaleString()}${unit}`}
                                            </strong>
                                        </div>
                                        <div className="roi-widget__bar-container roi-widget__bar-container--previous">
                                            <div
                                                className="roi-widget__bar roi-widget__bar--previous"
                                                style={{
                                                    width:
                                                        maxValue > 0
                                                            ? `${(previousValue / maxValue) * 100}%`
                                                            : "0%",
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    };

                    // Check if we should render charts instead of metrics
                    const visualizationMode = configData?.visualizationMode?.value || "numeric";

                    // Render charts if visualization mode is set to charts
                    if (visualizationMode === "line_chart" || visualizationMode === "bar_chart") {
                        const chartType = visualizationMode === "line_chart" ? "line" : "bar";
                        const selectedMetric = configData?.metric?.value || "time_saved";

                        // Map metric to chart metric format
                        let chartMetric: "count" | "timeSaved" | "costSaved";
                        switch (selectedMetric) {
                            case "tasks_automated":
                                chartMetric = "count";
                                break;
                            case "cost_savings":
                                chartMetric = "costSaved";
                                break;
                            default: // time_saved
                                chartMetric = "timeSaved";
                        }

                        return (
                            <div className="roi-widget__chart-wrapper">
                                <ROIChartContainer
                                    currentData={roiData.dailyActivities || []}
                                    previousData={roiData.comparison?.previousData?.dailyActivities}
                                    chartType={chartType}
                                    metric={chartMetric}
                                    currency={roiData.currency}
                                    showComparison={!!roiData.comparison?.enabled}
                                    height={250}
                                />
                            </div>
                        );
                    }

                    // Default to numeric view
                    return <div className="roi-widget__metrics">{renderMetric()}</div>;
                })();

            case ContentType.ERROR:
                return (
                    <WidgetEmpty
                        isEditAllowed={isEditAllowed}
                        showSettingsModal={handleConfigShow}
                        text={error || "Something went wrong."}
                    />
                );

            default:
                return null;
        }
    };

    // Simple fallback render for debugging
    if (!data) {
        return (
            <div className="glu-dashboard-card glu-content" style={{padding: "20px"}}>
                <h3>ROI Widget - No Data</h3>
                <p>Widget data is missing</p>
            </div>
        );
    }

    return (
        <>
            <div className="glu-dashboard-card glu-content roi-widget">
                <WidgetHeader
                    isEditAllowed={isEditAllowed}
                    title={data?.title || configData.title || "ROI Widget"}
                    height={height}
                    width={width}
                    showWidgetInfo={showWidgetInfo && showWidgetInfo(data)}
                    openConfiguration={handleConfigShow}
                    onRemove={onRemove && onRemove(data)}
                    onClone={onClone && onClone(data)}
                >
                    <WidgetHeaderButton
                        showButton={
                            Object.keys(configData).length > 0 &&
                            !isEditAllowed &&
                            !isUserWithDashboardViewerRole()
                        }
                        iconName="filter"
                        onClick={handleFilterShow}
                        hoverTooltip="Filter ROI data"
                        customClassName=""
                    />
                    <WidgetHeaderButton
                        showButton={
                            Object.keys(configData).length > 0 &&
                            !isEditAllowed &&
                            !isUserWithDashboardViewerRole()
                        }
                        iconName="download"
                        onClick={isDownloading ? undefined : handleDirectDownload}
                        hoverTooltip={isDownloading ? "Generating report..." : "Download report"}
                        customClassName={isDownloading ? "downloading" : ""}
                    />
                    <WidgetHeaderButton
                        showButton={
                            Object.keys(configData).length > 0 &&
                            !isEditAllowed &&
                            !isUserWithDashboardViewerRole()
                        }
                        iconName="refresh"
                        onClick={() => {
                            refreshData(configData);
                        }}
                        hoverTooltip="Refresh ROI data"
                        customClassName=""
                    />
                </WidgetHeader>

                {/* Date Range Selector - only show when widget has data or is configured */}
                {(contentType === ContentType.DATA || Object.keys(configData).length > 0) && (
                    <div className="glu-content__section">
                        <DateRangeSelector
                            selectedRange={dateRange}
                            onRangeChange={handleDateRangeChange}
                            initialPeriod={(configData.dateRange?.period as any) || "S"}
                        />
                    </div>
                )}

                {isLoading && <Loader />}
                <div className="glu-content__section glu-content__separator glu-content__separator--inner glu-spread-v">
                    {!isLoading && renderContent()}
                </div>
            </div>

            {showConfigModal && (
                <ModalDefault isOpen={showConfigModal}>
                    <div className="glu-modal-content">
                        <div className="glu-modal__header">
                            <div className="glu-modal__title glu-flex-grow">Widget Settings</div>
                            <button
                                type="button"
                                className="glu-modal__close"
                                onClick={handleConfigCancel}
                            >
                                <span className="g-icon g-icon-close" />
                            </button>
                        </div>
                        <ROIConfigurationForm
                            initialValues={tempConfigData}
                            tempConfigData={tempConfigData}
                            onFormSubmit={handleConfigSubmit}
                            onCancel={handleConfigCancel}
                            handleFormFieldChange={handleFormFieldChange}
                        />
                    </div>
                </ModalDefault>
            )}

            <ROIFilterModal
                isOpen={showFilterModal}
                onClose={handleFilterClose}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
                initialFilters={filterValues}
            />
        </>
    );
};

export default withResizeDetector(ROIWidget);
